import imageCompression from 'browser-image-compression';

import type {
  ClientEntity,
  ClientPaymentConfig,
  CustomerInvoice,
  CustomerProfile,
  GlobalSettings,
  Payment,
  Showcase,
} from '../types';
import { paraSlug, preencherTemplate, renderizarSite, templatePorId } from '@nyroh/contracts';
import type {
  ValoresTemplate,
  Fatura as FaturaContrato,
  Pagamento as PagamentoContrato,
  PerfilConsumidor as PerfilContrato,
  VersaoSite,
  Vitrine as VitrineContrato,
} from '@nyroh/contracts';

import { api, CDN_ASSETS, enviarImagem, ErroApiNyroh, guardarTokenDoPortal, tokenDoPortal } from '../lib/api';
import { htmlDoSpec, paraClientEntity, paraCriarCliente, specDeHtmlBruto } from './mapeadores';

/**
 * ============================================================================
 * CAMADA DE DADOS DO PAINEL
 * ============================================================================
 *
 * MESMA SUPERFICIE, OUTRO TRANSPORTE.
 *
 * Todo metodo aqui tem exatamente o nome, os parametros e o tipo de retorno que
 * tinha antes. As telas — `ClientDashboard.tsx` (1.338 linhas),
 * `ClientDossierManager.tsx` (1.321) e mais uma duzia — nao foram tocadas, e
 * essa foi a condicao: nao ha teste automatizado no repositorio para segurar
 * comportamento nao documentado, entao a superficie e o contrato.
 *
 * O QUE MUDOU POR BAIXO
 *
 *   antes                                  agora
 *   ─────────────────────────────────────  ──────────────────────────────────
 *   createClient(URL, ANON_KEY)            api.chamar(...) — sem credencial de
 *   no bundle do navegador                 banco no bundle
 *
 *   select('*') em `clients`,              colunas explicitas; o HTML nem mora
 *   arrastando html_content de 300 KB      mais nessa tabela
 *
 *   .range(from, to) em blocos de 100,     paginacao por cursor, custo constante
 *   ate mil requisicoes por carga          por pagina
 *
 *   filtro de busca aplicado NO NAVEGADOR  filtro no banco, com indice
 *   depois de baixar tudo
 *
 *   PIN comparado como filtro de query     POST + argon2id no servidor, com
 *                                          bloqueio por tentativa
 *
 *   html_content dentro do Postgres        artefato no R2, servido pela borda
 *
 * O arquivo antigo esta em `db.supabase-legado.ts.bak` enquanto a producao velha
 * nao for desligada. Ele nao e importado por ninguem.
 */

/** Cache de HTML por slug. Evita rebaixar o CDN a cada re-render da mesma tela. */
const cacheHtml = new Map<string, { html: string; quando: number }>();
const TTL_CACHE_HTML = 30_000;

/**
 * Busca o HTML publicado no CDN.
 *
 * E aqui que o desenho novo aparece na pratica: o painel le o artefato do
 * OBJECT STORAGE, nao do banco. O Postgres nao participa desta leitura.
 */
/**
 * O HTML da versao publicada, lido PELA API.
 *
 * Antes vinha direto do CDN: `VITE_CDN_BASE_SITES + chaveR2`. Isso exigia que o
 * bucket de sites fosse publico E que a variavel apontasse para o dominio
 * ligado a ELE — e um dominio de R2 serve um bucket so. Com as duas bases de
 * CDN no mesmo endereco, a leitura caia no bucket de imagens e voltava
 * `404 Object not found` com o objeto intacto no bucket certo. Nenhuma parte
 * dessa mensagem apontava para a causa.
 *
 * Quem le agora e a API, que ja tem credencial do R2 e ja sabe qual balde e
 * qual. Uma variavel a menos para errar, e o bucket de sites nao precisa mais
 * ser publico.
 *
 * Isso NAO muda quem serve os visitantes: o Worker da borda continua lendo o R2
 * direto, por binding, sem passar pela API.
 */
async function buscarHtmlPublicado(
  slug: string,
  siteId?: string,
  versaoId?: string,
): Promise<string> {
  if (!siteId || !versaoId) return '';

  const agora = Date.now();
  const emCache = cacheHtml.get(slug);
  if (emCache && agora - emCache.quando < TTL_CACHE_HTML) return emCache.html;

  try {
    const r = await api.chamar('sites.obterHtmlVersao', {
      params: { id: siteId, versaoId },
    });
    cacheHtml.set(slug, { html: r.html, quando: agora });
    return r.html;
  } catch (e) {
    // Falha aqui nao pode derrubar a tela: o restante do cadastro continua util
    // sem a previa. Mas nao passa em silencio.
    console.warn(`[db] nao consegui ler o HTML publicado de "${slug}":`, e);
    return '';
  }
}

/** Carrega cliente + HTML da versao publicada, na forma que as telas esperam. */
async function clienteCompleto(
  cliente: Awaited<ReturnType<typeof api.chamar<'clientes.obter'>>>,
): Promise<ClientEntity> {
  let html = '';
  // O site carrega junto — e dele que saem o endereco publico de verdade
  // (`urlPublica`) e o ponteiro da versao no ar. Antes essa chamada era feita e
  // o resultado, jogado fora: so o HTML sobrevivia, e as telas voltavam a
  // inventar o endereco a partir de `window.location`.
  let site: Awaited<ReturnType<typeof api.chamar<'sites.obter'>>> | null = null;
  if (cliente.siteId) {
    try {
      site = await api.chamar('sites.obter', { params: { id: cliente.siteId } });
      if (site.versaoAtualId) {
        const versoes = await api.chamar('sites.listarVersoes', {
          params: { id: cliente.siteId },
        });
        const corrente = versoes.find((v) => v.id === site!.versaoAtualId);
        if (corrente) html = await buscarHtmlPublicado(cliente.slug, cliente.siteId, corrente.id);
      }
    } catch (e) {
      if (!(e instanceof ErroApiNyroh && e.naoEncontrado)) throw e;
    }
  }
  return paraClientEntity(cliente, html, site);
}

/** Traduz o filtro de data do painel para o vocabulario do contrato. */
function periodoDoFiltro(
  dateFilter: string,
  inicio: string,
  fim: string,
): { periodo: 'hoje' | '7d' | '30d' | 'todos' | 'custom'; de?: string; ate?: string } {
  switch (dateFilter) {
    case 'today':
      return { periodo: 'hoje' };
    case 'yesterday': {
      // "Ontem" nao tem equivalente direto no contrato; vira uma janela custom de
      // um dia. Traduzir aqui e melhor que inventar mais um valor de enum que so
      // uma tela usa.
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const ontem = new Date(hoje.getTime() - 86_400_000);
      return {
        periodo: 'custom',
        de: ontem.toISOString().slice(0, 10),
        ate: ontem.toISOString().slice(0, 10),
      };
    }
    case 'week':
      return { periodo: '7d' };
    case 'custom':
      return inicio && fim ? { periodo: 'custom', de: inicio, ate: fim } : { periodo: 'todos' };
    default:
      return { periodo: 'todos' };
  }
}

/**
 * Troca o indice do item pelo nome do projeto na mensagem de erro.
 *
 * `clientes[93].slug: Too big` e verdadeiro e inutil para quem esta olhando uma
 * lista de 135 nomes. Vira "221 Arquitetura e Coworking (item 94)".
 */
function comNomeDoProjeto(erro: unknown, lote: ClientEntity[]): unknown {
  if (!(erro instanceof ErroApiNyroh) || erro.status !== 422) return erro;

  const issues = Array.isArray(erro.detalhes) ? erro.detalhes : [];
  const linhas = issues
    .map((i) => {
      const caminho = (i as { path?: PropertyKey[] }).path ?? [];
      const mensagem = (i as { message?: string }).message ?? 'invalido';
      const indice = caminho.find((p) => typeof p === 'number');
      const campo = caminho.filter((p) => typeof p === 'string' && p !== 'clientes').join('.');
      if (typeof indice !== 'number') return `${campo || '(raiz)'}: ${mensagem}`;

      const projeto = lote[indice];
      const nome = projeto?.data?.name || projeto?.slug || `item ${indice + 1}`;
      return `"${nome}" (item ${indice + 1}) — ${campo || 'campo'}: ${mensagem}`;
    })
    .slice(0, 5);

  if (linhas.length === 0) return erro;

  const resto = issues.length - linhas.length;
  return new ErroApiNyroh(
    erro.status,
    erro.codigo,
    `Estes projetos não passaram na validação e por isso nenhum foi importado:\n` +
      linhas.map((l) => `• ${l}`).join('\n') +
      (resto > 0 ? `\n• e mais ${resto}` : ''),
    erro.detalhes,
    erro.rota,
  );
}

export const db = {
  // ===== ACESSO DO CLIENTE ===== //

  /**
   * Login do cliente no portal.
   *
   * Antes: `select('*')` com `ILIKE` no slug como pre-filtro, mais os campos
   * `data->>username` e `data->>pin` como filtros de query — ou seja, o PIN
   * viajava na URL da consulta, e o HTML inteiro vinha junto a cada tentativa.
   * Pior: um cliente cujo slug nao casasse com o padrao derivado do proprio
   * usuario simplesmente nao conseguia entrar, e a tela dizia "PIN incorreto".
   *
   * Agora: POST, comparacao argon2id no servidor, bloqueio progressivo por
   * tentativa, e busca por indice unico em `usuario`.
   */
  loginClient: async (username: string, pin: string): Promise<ClientEntity | undefined> => {
    try {
      const sessao = await api.chamar('portal.entrar', {
        corpo: { usuario: username.trim(), pin: pin.trim() },
      });
      guardarTokenDoPortal(sessao.token);
      const cliente = await api.chamar('clientes.obter', { params: { id: sessao.clienteId } });
      const entidade = await clienteCompleto(cliente);
      // A tela precisa saber se a senha ainda e a de fabrica para levar direto
      // a troca. Vem do servidor junto com a sessao; nao e deduzido aqui.
      return { ...entidade, precisaTrocarSenha: sessao.precisaTrocarSenha };
    } catch (e) {
      if (e instanceof ErroApiNyroh && (e.precisaAutenticar || e.status === 429)) {
        // Credencial errada e excesso de tentativa sao respostas legitimas, nao
        // falhas: a tela mostra a mensagem do servidor.
        return undefined;
      }
      throw e;
    }
  },

  /**
   * Retoma a sessao do portal guardada no navegador.
   *
   * `portal.sessao` esta no contrato desde o inicio e nao era chamada por
   * ninguem: o cliente entrava, apertava F5 e voltava para o login, ainda que o
   * token continuasse valido no `sessionStorage` E no banco. A sessao existia; a
   * TELA e que nao perguntava.
   *
   * Devolve `undefined` quando nao ha token, quando ele expirou ou quando o
   * servidor recusa — nos tres casos o caminho e o mesmo, o login.
   */
  restoreClientSession: async (): Promise<ClientEntity | undefined> => {
    if (!tokenDoPortal()) return undefined;
    try {
      const sessao = await api.chamar('portal.sessao', {});
      const cliente = await api.chamar('clientes.obter', { params: { id: sessao.clienteId } });
      const entidade = await clienteCompleto(cliente);
      return { ...entidade, precisaTrocarSenha: sessao.precisaTrocarSenha };
    } catch (e) {
      if (e instanceof ErroApiNyroh && (e.precisaAutenticar || e.status === 403)) {
        // Token morto. O `aoErro` do cliente HTTP ja limpa o que estava guardado
        // em caso de 401; aqui garantimos o mesmo para 403.
        guardarTokenDoPortal(null);
        return undefined;
      }
      throw e;
    }
  },

  /**
   * Encerra a sessao do portal NO SERVIDOR.
   *
   * Sair so apagava o token do `sessionStorage`: a linha continuava valida em
   * `sessoes_portal` ate expirar, e quem tivesse copiado o token seguia dentro.
   */
  logoutClient: async (): Promise<void> => {
    try {
      await api.chamar('portal.sair', {});
    } catch (e) {
      // Token ja expirado, ou operador sem sessao de portal. Sair nunca pode
      // falhar para o usuario — mas tambem nao passa em silencio.
      console.warn('[db] falha ao encerrar a sessao do portal no servidor:', e);
    } finally {
      guardarTokenDoPortal(null);
    }
  },

  /**
   * O cliente troca a propria senha.
   *
   * Devolve `null` em caso de sucesso e a mensagem do servidor quando ele
   * recusa (senha atual errada, nova igual a atual, nova igual a padrao). Erro
   * de rede continua subindo: a tela nao deve dizer "senha incorreta" quando o
   * que houve foi queda de conexao — era assim que o login errava o diagnostico.
   */
  changePortalPassword: async (
    senhaAtual: string,
    novaSenha: string,
  ): Promise<string | null> => {
    try {
      await api.chamar('portal.trocarSenha', { corpo: { senhaAtual, novaSenha } });
      return null;
    } catch (e) {
      if (e instanceof ErroApiNyroh && (e.status === 401 || e.status === 422)) {
        return e.message;
      }
      throw e;
    }
  },

  /**
   * O HTML que o editor deve abrir, e a VERDADE sobre de onde ele veio.
   *
   * `buscarHtmlPublicado` engole a falha e devolve `''` (db.ts, `catch` do
   * `sites.obterHtmlVersao`). Para a tela de cadastro isso e certo — a previa e
   * dispensavel. Para o EDITOR e perigoso: `''` de "R2 fora do ar" e
   * indistinguivel de `''` de "cliente sem site", e no segundo caso o editor
   * oferece publicar um rascunho — por cima de um site que existe e que nao
   * conseguimos ler.
   *
   * Por isso aqui a falha e um estado nomeado, nao uma string vazia. E por isso
   * o RASCUNHO tambem: um site criado e nunca publicado tem spec e nao tem
   * versao no ar; abrir o rascunho da fabrica em vez do spec dele apagaria o
   * trabalho no primeiro "Publicacao Final".
   */
  getSiteHtmlParaEditar: async (
    cliente: ClientEntity,
  ): Promise<
    | { origem: 'publicado' | 'rascunho'; html: string; specTipado?: boolean }
    | { origem: 'sem-site' }
    | { origem: 'falha'; motivo: string }
  > => {
    if (!cliente.siteId) return { origem: 'sem-site' };

    try {
      const site = await api.chamar('sites.obter', { params: { id: cliente.siteId } });

      if (site.versaoAtualId) {
        const r = await api.chamar('sites.obterHtmlVersao', {
          params: { id: site.id, versaoId: site.versaoAtualId },
        });
        return { origem: 'publicado', html: r.html };
      }

      // Site existe e nunca foi publicado: o conteudo vive no spec.
      const spec = await api.chamar('sites.obterSpec', { params: { id: site.id } });

      // Spec de HTML importado: o documento esta la dentro, inteiro.
      const bruto = htmlDoSpec(spec);
      if (bruto) return { origem: 'rascunho', html: bruto };

      /*
       * Spec TIPADO (hero, sobre, servicos...). E o que a fabrica e o seed
       * produzem, e e o desenho certo — mas o editor visual trabalha com a
       * string de HTML inteira.
       *
       * Renderizar aqui, com a MESMA funcao que o servidor usa, e o unico jeito
       * de mostrar o site de verdade em vez de um rascunho generico. O preco
       * esta declarado na tela: salvar por este editor achata o spec tipado em
       * `htmlBruto`, que e o item 2 da secao 4.2 do CONTINUAR-AQUI. Esconder o
       * site para nao pagar esse preco seria pior — a pessoa publicaria o
       * rascunho por cima do que ja existe.
       */
      return {
        origem: 'rascunho',
        html: renderizarSite(spec, { baseCdn: CDN_ASSETS, urlCanonica: cliente.urlPublica ?? undefined }),
        specTipado: true,
      };
    } catch (e) {
      if (e instanceof ErroApiNyroh && e.naoEncontrado) return { origem: 'sem-site' };
      console.error('[db] falha ao ler o site para edicao:', e);
      return {
        origem: 'falha',
        motivo: e instanceof Error ? e.message : 'erro desconhecido',
      };
    }
  },

  /** Estado da credencial de um cliente, para o painel do operador. */
  getClientCredential: async (clientId: string) =>
    api.chamar('clientes.credencial', { params: { id: clientId } }),

  /**
   * Cria ou redefine a credencial do portal de um cliente.
   *
   * Substitui o `handleGenerateCredentials` que sorteava um PIN com
   * `Math.random()` no navegador e o gravava em texto puro dentro do JSONB de
   * negocio — de onde `updateClientData` o descartava, porque o corpo de
   * `clientes.atualizar` nao tem campo de credencial. O clique parecia funcionar
   * e nao criava conta nenhuma.
   *
   * A senha em claro vem na resposta UMA vez, para o operador repassar.
   */
  setClientCredential: async (
    clientId: string,
    dados: { usuario?: string; senha?: string } = {},
  ) =>
    api.chamar('clientes.definirCredencial', {
      params: { id: clientId },
      corpo: dados,
    }),

  // ===== CLIENTES ===== //

  getClients: async (
    searchTerm?: string,
    limitCount = 300,
    dateFilter = 'today',
    customStartDate = '',
    customEndDate = '',
  ): Promise<ClientEntity[]> => {
    const janela = periodoDoFiltro(dateFilter, customStartDate, customEndDate);
    const itens: ClientEntity[] = [];
    let cursor: string | undefined;

    // Pagina por CURSOR ate atingir o limite pedido.
    //
    // A diferenca para o `.range()` de antes nao e estilo: `range` vira OFFSET, e
    // o banco relê e descarta todas as linhas anteriores a cada bloco. Com 100
    // mil clientes, a ultima pagina lia a tabela inteira. Com cursor, cada pagina
    // custa o mesmo.
    while (itens.length < limitCount) {
      const pagina = await api.chamar('clientes.listar', {
        query: {
          ...janela,
          busca: searchTerm?.trim() || undefined,
          cursor,
          limite: Math.min(200, limitCount - itens.length),
          comDadosNegocio: false,
        },
      });
      // `htmlContent` fica vazio de proposito: listagem nunca arrasta artefato.
      for (const c of pagina.itens) itens.push(paraClientEntity(c));
      if (!pagina.proximoCursor) break;
      cursor = pagina.proximoCursor;
    }

    return itens;
  },

  getTotalClientsCount: async (): Promise<number> => {
    const { total } = await api.chamar('clientes.contar', { query: { periodo: 'todos' } });
    return total;
  },

  getAllClientsForExport: async (): Promise<any[]> => {
    // Limite duro do lado do servidor (5.000). Antes isto paginava a tabela
    // INTEIRA ate esgotar, com o HTML junto — um clique na interface conseguia
    // derrubar o banco.
    const linhas = await api.chamar('clientes.exportar', { query: { periodo: 'todos' } });

    // Devolve com `data` aninhado porque e assim que a tela de exportacao le
    // (`client.data?.name`, `client.data?.placeUri`). Achatar aqui produziria um
    // JSON de exportacao cheio de "Sem nome" — o tipo de regressao que passa
    // despercebida ate alguem abrir o arquivo.
    return linhas.map((l) => {
      const r = l as Record<string, unknown>;
      return {
        id: r.slug,
        slug: r.slug,
        data: {
          id: r.slug,
          name: r.nome,
          phoneNumber: r.telefone ?? '',
          placeUri: '',
          categories: r.categoria ? [r.categoria] : [],
          createdAt: r.criadoEm,
        },
        status: r.status,
      };
    });
  },

  /**
   * Importa clientes em lote.
   *
   * O nome do metodo e o retorno sao os de antes. O que sumiu foi o laco de
   * "fallback individual para resolver o erro 23505": o `ON CONFLICT DO NOTHING`
   * do servidor resolve numa ida so.
   */
  saveClients: async (
    newClients: ClientEntity[],
  ): Promise<{ saved: number; skipped: number }> => {
    let saved = 0;
    let skipped = 0;

    // Lotes de 500 — o teto que o contrato aceita por requisicao.
    for (let i = 0; i < newClients.length; i += 500) {
      const original = newClients.slice(i, i + 500);
      const lote = original.map(paraCriarCliente);
      try {
        const r = await api.chamar('clientes.importar', { corpo: { clientes: lote } });
        saved += r.salvos;
        skipped += r.ignorados;
      } catch (e) {
        // O contrato recusa o LOTE inteiro quando um item nao passa — e ele diz
        // isso por indice: `clientes[93].slug`. Indice nao ajuda ninguem a achar
        // o projeto numa lista de 135 nomes. Aqui o indice vira nome.
        throw comNomeDoProjeto(e, original);
      }
    }
    return { saved, skipped };
  },

  getClientById: async (id: string): Promise<ClientEntity | undefined> => {
    try {
      const cliente = await api.chamar('clientes.obter', { params: { id } });
      return await clienteCompleto(cliente);
    } catch (e) {
      if (e instanceof ErroApiNyroh && e.naoEncontrado) return undefined;
      throw e;
    }
  },

  getClientBySlug: async (slug: string): Promise<ClientEntity | undefined> => {
    try {
      const cliente = await api.chamar('clientes.obterPorSlug', {
        params: { slug: slug.toLowerCase().trim() },
      });
      return await clienteCompleto(cliente);
    } catch (e) {
      if (e instanceof ErroApiNyroh && e.naoEncontrado) return undefined;
      throw e;
    }
  },

  /**
   * Resolve um dominio proprio para o cliente dono.
   *
   * Antes usava `.single()` sobre uma coluna sem restricao de unicidade — com
   * dois clientes apontando para o mesmo dominio (BUG-4), a consulta falhava ou
   * devolvia um deles arbitrariamente. Agora `hostname` e PRIMARY KEY: o dado
   * ruim nao existe.
   */
  getClientByDomain: async (domain: string): Promise<ClientEntity | undefined> => {
    try {
      const r = await api.chamar('hostnames.resolver', {
        params: { hostname: domain.toLowerCase().trim() },
      });
      const site = await api.chamar('sites.obter', { params: { id: r.siteId } });
      const cliente = await api.chamar('clientes.obter', { params: { id: site.clienteId } });
      const html = site.versaoAtualId
        ? await buscarHtmlPublicado(cliente.slug, site.id, site.versaoAtualId)
        : '';
      return paraClientEntity(cliente, html, site);
    } catch (e) {
      if (e instanceof ErroApiNyroh && (e.naoEncontrado || e.status === 410)) return undefined;
      throw e;
    }
  },

  deleteClients: async (ids: string[]): Promise<void> => {
    await api.chamar('clientes.excluir', { corpo: { ids } });
  },

  /**
   * Salva o HTML editado.
   *
   * Aqui esta a virada de chave da arquitetura, num metodo so: o HTML NAO vai
   * mais para uma coluna do Postgres. Ele e renderizado num artefato imutavel,
   * gravado no R2 com `Cache-Control: immutable`, e o banco guarda so o ponteiro
   * e o hash.
   *
   * Consequencia visivel: salvar duas vezes o mesmo conteudo nao escreve nada —
   * o `content_hash` corta a operacao antes do R2.
   */
  /**
   * Devolve o resultado da publicacao em vez de `void`.
   *
   * `sites.publicar` compara o sha256 com a versao corrente e, quando bate,
   * devolve `escreveuNoR2: false` sem criar versao nem invalidar a borda — o
   * comportamento certo, e a razao de o bucket nao encher de copias. Mas com
   * retorno `void` a tela nao tinha como saber, e dizia "publicado" mesmo
   * quando nada mudou. Quem so trocou um espaco ficava procurando a versao
   * nova no historico.
   */
  updateClientSite: async (
    slug: string,
    newHtmlContent: string,
  ): Promise<{ mudou: boolean }> => {
    const limpo = slug.toLowerCase().trim();
    const cliente = await api.chamar('clientes.obterPorSlug', { params: { slug: limpo } });

    let siteId = cliente.siteId;
    if (!siteId) {
      const criado = await api.chamar('sites.criar', {
        corpo: {
          clienteId: cliente.id,
          slug: limpo,
          spec: specDeHtmlBruto(newHtmlContent, { nome: cliente.nome, slug: limpo }),
        },
      });
      siteId = criado.id;
    }

    const r = await api.chamar('sites.publicar', {
      params: { id: siteId },
      corpo: {
        spec: specDeHtmlBruto(newHtmlContent, { nome: cliente.nome, slug: limpo }),
        geradoPor: 'editor-painel',
        apenasRascunho: false,
      },
    });

    cacheHtml.delete(limpo);
    return { mudou: r.escreveuNoR2 };
  },

  /**
   * ==========================================================================
   * APLICAR UM MODELO — publica SPEC TIPADO, nao HTML bruto
   * ==========================================================================
   *
   * `updateClientSite` embrulha o HTML numa unica secao `htmlBruto`, que e a
   * escotilha do acervo importado: o spec fica sendo uma string opaca de 12 KB
   * que ninguem consulta por dentro. Aplicar um modelo faz o contrario — grava
   * o spec com as SECOES tipadas, que e o formato que a secao 08 do diagnostico
   * escolheu.
   *
   * A diferenca e concreta e nao e estetica: com secoes tipadas, melhorar o
   * template no contrato regenera este site de graca; com `htmlBruto`, o unico
   * caminho e alguem reescrever o HTML na mao.
   *
   * Isto NAO apaga o que estava no ar. `sites.publicar` cria uma versao nova e
   * vira o ponteiro; o artefato anterior continua no R2 e volta por
   * `sites.reverter`. E por isso que a tela pode oferecer "experimente outro
   * modelo" sem ser um caminho sem volta.
   */
  aplicarTemplate: async (
    slug: string,
    templateId: string,
    valores: ValoresTemplate,
  ): Promise<void> => {
    const limpo = slug.toLowerCase().trim();
    const template = templatePorId(templateId);
    if (!template) throw new Error(`Modelo desconhecido: ${templateId}`);

    const cliente = await api.chamar('clientes.obterPorSlug', { params: { slug: limpo } });
    const spec = preencherTemplate(template, valores);

    let siteId = cliente.siteId;
    if (!siteId) {
      const criado = await api.chamar('sites.criar', {
        corpo: { clienteId: cliente.id, slug: limpo, spec },
      });
      siteId = criado.id;
    }

    await api.chamar('sites.publicar', {
      params: { id: siteId },
      // `geradoPor` diz QUAL modelo produziu isto — e o que faz o historico de
      // versoes responder "de onde veio esta pagina" sem abrir o artefato.
      corpo: { spec, geradoPor: `template:${template.id}`, apenasRascunho: false },
    });

    // O HTML em cache e o antigo. Sem isto, reabrir o codigo-fonte logo depois
    // de aplicar mostraria a pagina anterior e faria o proximo salvar publicar
    // de volta o que acabou de ser trocado.
    cacheHtml.delete(limpo);
  },

  /**
   * O historico de versoes de um site, com o ponteiro do que esta no ar.
   *
   * Existe porque publicar passou a criar um ARTEFATO IMUTAVEL por versao: o
   * R2 acumula `sites/{siteId}/{versaoId}.html`, um por publicacao. Isso e o
   * desenho — permite voltar sem regerar nada — mas ate agora o painel nao
   * mostrava nenhum deles, e quem olhava o bucket via dois arquivos sem saber
   * qual estava servindo.
   */
  listSiteVersions: async (
    siteId: string,
  ): Promise<{ versoes: VersaoSite[]; versaoAtualId: string | null }> => {
    const [site, versoes] = await Promise.all([
      api.chamar('sites.obter', { params: { id: siteId } }),
      api.chamar('sites.listarVersoes', { params: { id: siteId } }),
    ]);
    // Mais nova primeiro: e a ordem em que a pessoa procura.
    const ordenadas = [...versoes].sort(
      (a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime(),
    );
    return { versoes: ordenadas, versaoAtualId: site.versaoAtualId };
  },

  /**
   * Troca a versao no ar.
   *
   * NAO regera nada: o artefato antigo continua no R2 exatamente como foi
   * publicado, e o que muda e o ponteiro `versaoAtualId` mais a entrada do KV
   * da borda. E por isso que voltar uma versao e instantaneo e nao depende de
   * o gerador ainda produzir o mesmo HTML.
   */
  activateSiteVersion: async (siteId: string, versaoId: string, slug: string): Promise<void> => {
    await api.chamar('sites.reverter', {
      params: { id: siteId },
      corpo: { versaoId },
    });
    cacheHtml.delete(slug.toLowerCase().trim());
  },

  /** O HTML de UMA versao especifica, lido pela API. Nao usa o cache por slug. */
  getVersionHtml: async (siteId: string, versaoId: string): Promise<string> => {
    const r = await api.chamar('sites.obterHtmlVersao', {
      params: { id: siteId, versaoId },
    });
    return r.html;
  },

  /**
   * Envia uma imagem.
   *
   * A compressao no navegador continua (300 KB, 1920px, WebP) — ela e boa e
   * economiza banda de quem sobe. O que mudou e o destino: URL pre-assinada
   * direto para o R2, sem passar pela API.
   *
   * O bucket `uploads` do Supabase era publico para ESCRITA anonima: qualquer
   * visitante trocava a logo de um cliente ou apagava as imagens de todos os
   * sites. Aqui a escrita exige autorizacao do servidor; publica e so a leitura.
   */
  uploadAsset: async (
    file: File,
    /**
     * A quem a imagem pertence.
     *
     * Opcional para o operador, que ja passa pelo guard por cargo. Do PORTAL do
     * cliente e obrigatorio: `assets.assinarUpload` e `assets.registrar` sao
     * `acesso: 'cliente'` com `dono: 'destino:corpo'`, e sem destino declarado o
     * guard nao tem como conferir de quem e o upload — entao recusa. Era assim
     * que o bucket `uploads` do Supabase aceitava escrita anonima.
     */
    destino: { clienteId?: string; siteId?: string } = {},
  ): Promise<string> => {
    const comprimido = await imageCompression(file, {
      maxSizeMB: 0.3,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/webp',
    });
    const nomeLimpo =
      (file.name.split('.')[0] ?? 'img').replace(/[^a-zA-Z0-9_]/g, '') || 'img';
    const arquivo = new File([comprimido], `${nomeLimpo}.webp`, { type: 'image/webp' });

    const { url } = await enviarImagem(arquivo, destino);
    return url;
  },

  updateClientSlug: async (
    id: string,
    newSlug: string,
  ): Promise<{ success: boolean; error?: string }> => {
    // Mesma regra do servidor e do `slugSchema` — esta era a quinta copia.
    const limpo = paraSlug(newSlug);
    if (!limpo || limpo.length < 3) return { success: false, error: 'Link inválido.' };

    try {
      await api.chamar('clientes.atualizar', { params: { id }, corpo: { slug: limpo } });
      return { success: true };
    } catch (e) {
      if (e instanceof ErroApiNyroh) {
        // 409 do servidor vem do UNIQUE no banco — que e a garantia de verdade.
        // A checagem anterior no front era corrida: dois operadores salvando ao
        // mesmo tempo passavam os dois.
        return {
          success: false,
          error: e.codigo === 'ja_existe' ? 'Link já está em uso.' : e.message,
        };
      }
      throw e;
    }
  },

  /**
   * Aponta (ou remove) o dominio proprio de um cliente.
   *
   * Vira `hostnames.adicionar` / `hostnames.remover`. Um site pode ter varios
   * dominios; o painel so expoe o principal, entao o principal anterior sai.
   */
  updateClientDomain: async (id: string, customDomain: string | null): Promise<void> => {
    const cliente = await api.chamar('clientes.obter', { params: { id } });
    if (!cliente.siteId) {
      throw new Error('Este cliente ainda não tem site. Publique o site antes de apontar o domínio.');
    }

    const atuais = await api.chamar('hostnames.listar', { params: { id: cliente.siteId } });
    for (const h of atuais.filter((x) => x.principal)) {
      await api.chamar('hostnames.remover', { params: { hostname: h.hostname } });
    }

    if (customDomain?.trim()) {
      await api.chamar('hostnames.adicionar', {
        params: { id: cliente.siteId },
        corpo: { hostname: customDomain.toLowerCase().trim(), principal: true },
      });
    }
  },

  updateClientData: async (id: string, newData: any): Promise<void> => {
    const d = newData as Record<string, never> & {
      name?: string;
      phoneNumber?: string;
      address?: string;
      categories?: string[];
      rating?: number;
      reviewCount?: number;
      summary?: string;
      seoKeywords?: string[];
      blueprint?: unknown[];
      website?: string | null;
      openingHours?: unknown;
      topReviews?: unknown[];
      amenities?: string[];
      photoAnalysis?: string;
      priceLevel?: string | null;
      businessStatus?: string;
    };

    await api.chamar('clientes.atualizar', {
      params: { id },
      corpo: {
        ...(d.name ? { nome: d.name } : {}),
        ...(d.phoneNumber !== undefined ? { telefone: d.phoneNumber } : {}),
        ...(d.address !== undefined ? { endereco: d.address } : {}),
        ...(d.categories?.[0] ? { categoria: d.categories[0] } : {}),
        ...(typeof d.rating === 'number' ? { nota: d.rating } : {}),
        ...(typeof d.reviewCount === 'number' ? { totalAvaliacoes: d.reviewCount } : {}),
        dadosNegocio: {
          resumo: d.summary ?? null,
          palavrasChaveSeo: d.seoKeywords ?? [],
          blueprint: (d.blueprint as never) ?? [],
          website: d.website ?? null,
          horarios: d.openingHours ?? null,
          avaliacoesTopo: (d.topReviews as never) ?? [],
          categorias: d.categories ?? [],
          comodidades: d.amenities ?? [],
          analiseFotos: d.photoAnalysis ?? null,
          faixaPreco: d.priceLevel ?? null,
          statusNegocio: d.businessStatus ?? null,
        },
      },
    });
  },

  updateClientPrice: async (id: string, customPrice: number | null): Promise<void> => {
    await api.chamar('clientes.atualizar', {
      params: { id },
      corpo: { precoPersonalizado: customPrice },
    });
  },

  // ===== CEREBRO DO NEGOCIO ===== //

  getClientBusinessBrain: async (clientId: string): Promise<any> => {
    if (!clientId) return null;
    return api.chamar('cerebro.obter', { params: { id: clientId } });
  },

  /**
   * Salva o dossie.
   *
   * A versao antiga gravava no `localStorage` como "cache/fallback" e engolia o
   * erro do banco com um `console.warn`, dizendo "salvo localmente (modo
   * offline)". Na pratica, o operador via "salvo" e o dado nao existia para mais
   * ninguem. Agora ou grava no servidor, ou o erro sobe para a tela.
   */
  saveClientBusinessBrain: async (clientId: string, stateData: any): Promise<void> => {
    if (!clientId) return;
    await api.chamar('cerebro.salvar', {
      params: { id: clientId },
      corpo: {
        dadosEmpresa: stateData.companyData,
        responsaveis: stateData.responsibles,
        detalhesNegocio: stateData.businessDetails,
        produtos: stateData.products,
        clienteIdeal: stateData.idealClient,
        concorrentes: stateData.competitors,
        processoVendas: stateData.salesProcess,
        crm: stateData.crmData,
        automacoes: stateData.automations,
        provasSociais: stateData.socialProofs,
        financeiro: stateData.financial,
        objetivos: stateData.objectives,
        documentosGerais: stateData.generalDocs,
        baseConhecimento: stateData.knowledgeBase,
        marca: stateData.brandingData,
        biblioteca: stateData.mediaLibrary,
        materiaisApoio: stateData.supportMaterials,
        conexoes: stateData.connections,
      },
    });
  },

  // ===== CONFIGURACAO GLOBAL ===== //

  getGlobalSettings: async (): Promise<GlobalSettings> => {
    const c = await api.chamar('configGlobal.obter', {});
    return {
      defaultSitePrice: c.precoPadraoSite,
      pixCnpj: c.pixCnpj,
      updatedAt: c.atualizadoEm ?? undefined,
    };
  },

  updateGlobalSettings: async (settings: Partial<GlobalSettings>): Promise<void> => {
    await api.chamar('configGlobal.salvar', {
      corpo: {
        ...(settings.defaultSitePrice !== undefined
          ? { precoPadraoSite: settings.defaultSitePrice }
          : {}),
        ...(settings.pixCnpj !== undefined ? { pixCnpj: settings.pixCnpj } : {}),
      },
    });
  },

  // ===== PAGAMENTOS (NYROH -> CLIENTE) ===== //

  createPayment: async (paymentData: Omit<Payment, 'id' | 'createdAt'>): Promise<Payment> => {
    const p = await api.chamar('pagamentos.criar', {
      corpo: {
        clienteId: paymentData.clientId,
        valor: paymentData.amount,
        metodo: paymentData.paymentMethod,
        status: paymentData.status,
        stripePaymentIntentId: paymentData.stripePaymentIntentId ?? null,
        urlComprovante: paymentData.receiptUrl ?? null,
      },
    });
    return paraPayment(p);
  },

  updatePaymentStatus: async (
    id: string,
    status: string,
    receiptUrl?: string,
  ): Promise<void> => {
    await api.chamar('pagamentos.atualizarStatus', {
      params: { id },
      corpo: { status: status as Payment['status'], urlComprovante: receiptUrl },
    });
  },

  getPaymentsByClient: async (clientId: string): Promise<Payment[]> => {
    const lista = await api.chamar('pagamentos.listar', { query: { clienteId: clientId } });
    return lista.map(paraPayment);
  },

  updateClientStatus: async (clientId: string, status: string): Promise<void> => {
    await api.chamar('clientes.atualizar', {
      params: { id: clientId },
      corpo: { status: status === 'active' ? 'ativo' : 'pendente' },
    });
  },

  getAllPayments: async (): Promise<Payment[]> => {
    const lista = await api.chamar('pagamentos.listar', { query: {} });
    return lista.map(paraPayment);
  },

  // ===== VITRINES ===== //
  //
  // Antes as vitrines eram linhas da tabela `clients` com `status = 'showcase'`
  // e os itens dentro do JSONB `data`. Isso significa que a tabela mais quente do
  // sistema guardava tres coisas diferentes. Agora sao tabelas proprias.

  getShowcases: async (): Promise<Showcase[]> => {
    const lista = await api.chamar('vitrines.listar', {});
    return lista.map(paraShowcase);
  },

  getShowcaseBySlug: async (slug: string): Promise<Showcase | undefined> => {
    try {
      const v = await api.chamar('vitrines.obterPorSlug', {
        params: { slug: slug.toLowerCase().trim() },
      });
      return paraShowcase(v);
    } catch (e) {
      if (e instanceof ErroApiNyroh && e.naoEncontrado) return undefined;
      throw e;
    }
  },

  saveShowcase: async (showcase: Showcase): Promise<Showcase> => {
    const v = await api.chamar('vitrines.salvar', {
      corpo: {
        // Id curto vinha de rascunho no navegador: o servidor gera o UUID.
        id: showcase.id && showcase.id.length >= 20 ? showcase.id : undefined,
        slug: showcase.slug.toLowerCase().trim(),
        nome: showcase.name,
        itens: showcase.items.map((i, ordem) => ({
          tipo: i.type,
          clienteId: i.clientId ?? null,
          urlExterna: i.externalUrl ?? null,
          tituloExterno: i.externalTitle ?? null,
          aba: i.tabCategory,
          ordem,
        })),
      },
    });
    return paraShowcase(v);
  },

  deleteShowcase: async (id: string): Promise<void> => {
    await api.chamar('vitrines.excluir', { params: { id } });
  },

  // ===== DOSSIE 360 DO CONSUMIDOR FINAL ===== //
  //
  // Antes: `localStorage` com quatro pessoas ficticias fixas como valor inicial.
  // Sumia ao trocar de navegador e nao existia para mais ninguem na operacao —
  // o operador achava que estava usando um CRM.

  getCustomerProfiles: async (clientId: string): Promise<CustomerProfile[]> => {
    const lista = await api.chamar('consumidores.listar', { params: { id: clientId } });
    return lista.map(paraCustomerProfile);
  },

  saveCustomerProfile: async (profile: CustomerProfile): Promise<CustomerProfile> => {
    const p = await api.chamar('consumidores.salvar', {
      params: { id: profile.clientId },
      corpo: {
        id: profile.id && profile.id.length >= 20 ? profile.id : undefined,
        clienteId: profile.clientId,
        nome: profile.name,
        telefone: profile.phone,
        email: profile.email ?? null,
        tipo: profile.type,
        status: profile.status,
        receitaTotal: profile.totalRevenue,
        pendenteTotal: profile.totalPending,
        servicos: profile.purchasedServices.map((s) => ({
          id: s.id,
          titulo: s.title,
          valor: s.amount,
          data: s.date,
          status: s.status,
          categoria: s.category,
        })),
        linhaTempo: profile.timeline.map((t) => ({
          id: t.id,
          tipo: t.type,
          titulo: t.title,
          descricao: t.description,
          quando: t.timestamp,
          valor: t.amount,
        })),
        observacoes: profile.notes ?? null,
        codigoAcessoPortal: profile.portalAccessCode ?? null,
        responsavel: profile.assignedTo ?? null,
        origem: profile.source ?? null,
      },
    });
    return paraCustomerProfile(p);
  },

  getCustomerByPhone: async (
    clientId: string,
    rawPhone: string,
  ): Promise<CustomerProfile | undefined> => {
    const alvo = rawPhone.replace(/[^0-9]/g, '');
    const perfis = await db.getCustomerProfiles(clientId);
    return perfis.find((p) => {
      const limpo = p.phone.replace(/[^0-9]/g, '');
      return limpo.includes(alvo) || alvo.includes(limpo);
    });
  },

  // ===== FATURAS (CLIENTE -> CONSUMIDOR FINAL) ===== //

  getCustomerInvoices: async (
    clientId: string,
    customerPhone?: string,
  ): Promise<CustomerInvoice[]> => {
    const pagina = await api.chamar('faturas.listar', {
      params: { id: clientId },
      query: { limite: 200, telefoneConsumidor: customerPhone },
    });
    return pagina.itens.map(paraCustomerInvoice);
  },

  getInvoiceById: async (invoiceId: string): Promise<CustomerInvoice | undefined> => {
    try {
      const f = await api.chamar('faturas.obter', { params: { id: invoiceId } });
      return paraCustomerInvoice(f);
    } catch (e) {
      if (e instanceof ErroApiNyroh && e.naoEncontrado) return undefined;
      throw e;
    }
  },

  createCustomerInvoice: async (
    invoiceData: Omit<CustomerInvoice, 'id' | 'createdAt'>,
  ): Promise<CustomerInvoice> => {
    const f = await api.chamar('faturas.criar', {
      params: { id: invoiceData.clientId },
      corpo: {
        clienteId: invoiceData.clientId,
        clienteSlug: invoiceData.clientSlug ?? null,
        telefoneConsumidor: invoiceData.customerPhone,
        nomeConsumidor: invoiceData.customerName,
        emailConsumidor: invoiceData.customerEmail ?? null,
        titulo: invoiceData.title,
        descricao: invoiceData.description ?? null,
        valor: invoiceData.amount,
        vencimento: invoiceData.dueDate,
        tipoMetodo: invoiceData.paymentMethodType ?? null,
        codigoPix: invoiceData.pixCode ?? null,
        urlPagamentoCartao: invoiceData.cardPaymentUrl ?? null,
        urlComprovante: invoiceData.receiptUrl ?? null,
      },
    });
    return paraCustomerInvoice(f);
  },

  payCustomerInvoice: async (
    invoiceId: string,
    method: 'pix' | 'card' | 'gateway' = 'pix',
    receiptUrl?: string,
  ): Promise<CustomerInvoice> => {
    const f = await api.chamar('faturas.pagar', {
      params: { id: invoiceId },
      corpo: { metodo: method, urlComprovante: receiptUrl },
    });
    return paraCustomerInvoice(f);
  },

  // ===== CONFIGURACAO DE PAGAMENTO DO CLIENTE ===== //

  getClientPaymentConfig: async (clientId: string): Promise<ClientPaymentConfig> => {
    const c = await api.chamar('configPagamento.obter', { params: { id: clientId } });
    return {
      clientId: c.clienteId,
      pixKeyType: c.tipoChavePix ?? undefined,
      pixKey: c.chavePix ?? undefined,
      pixReceiverName: c.nomeRecebedorPix ?? undefined,
      pixCity: c.cidadePix ?? undefined,
      cardPaymentLink: c.linkPagamentoCartao ?? undefined,
      cardLinkInstructions: c.instrucoesLinkCartao ?? undefined,
      gatewayProvider: c.provedorGateway ?? undefined,
      // O token do gateway NUNCA volta do servidor. A tela recebe so o booleano
      // de "esta configurado" — o valor cifrado nao sai do banco.
      gatewayToken: undefined,
      gatewayActive: c.gatewayAtivo,
      allowPixDirect: c.permitePixDireto,
      allowCardLink: c.permiteLinkCartao,
      updatedAt: c.atualizadoEm ?? undefined,
    };
  },

  saveClientPaymentConfig: async (
    config: ClientPaymentConfig,
  ): Promise<ClientPaymentConfig> => {
    await api.chamar('configPagamento.salvar', {
      params: { id: config.clientId },
      corpo: {
        tipoChavePix: config.pixKeyType ?? null,
        chavePix: config.pixKey ?? null,
        nomeRecebedorPix: config.pixReceiverName ?? null,
        cidadePix: config.pixCity ?? null,
        linkPagamentoCartao: config.cardPaymentLink ?? null,
        instrucoesLinkCartao: config.cardLinkInstructions ?? null,
        provedorGateway: config.gatewayProvider ?? null,
        // Só envia o token quando o operador digitou um novo. Mandar `undefined`
        // preserva o que ja esta gravado.
        ...(config.gatewayToken ? { gatewayToken: config.gatewayToken } : {}),
        gatewayAtivo: config.gatewayActive ?? false,
        permitePixDireto: config.allowPixDirect,
        permiteLinkCartao: config.allowCardLink,
      },
    });
    return db.getClientPaymentConfig(config.clientId);
  },
};

// ---------------------------------------------------------------------------
// Conversores para os tipos das telas
// ---------------------------------------------------------------------------

function paraPayment(p: PagamentoContrato): Payment {
  return {
    id: p.id,
    clientId: p.clienteId,
    amount: p.valor,
    paymentMethod: p.metodo as Payment['paymentMethod'],
    status: p.status as Payment['status'],
    stripePaymentIntentId: p.stripePaymentIntentId,
    receiptUrl: p.urlComprovante,
    createdAt: p.criadoEm,
    updatedAt: p.atualizadoEm ?? undefined,
  };
}

function paraShowcase(v: VitrineContrato): Showcase {
  return {
    id: v.id,
    slug: v.slug,
    name: v.nome,
    createdAt: v.criadoEm,
    items: v.itens.map((i) => ({
      id: i.id,
      type: i.tipo as 'internal' | 'external',
      clientId: i.clienteId ?? undefined,
      externalUrl: i.urlExterna ?? undefined,
      externalTitle: i.tituloExterno ?? undefined,
      tabCategory: i.aba,
    })),
  };
}

function paraCustomerProfile(p: PerfilContrato): CustomerProfile {
  return {
    id: p.id,
    clientId: p.clienteId,
    name: p.nome,
    phone: p.telefone,
    email: p.email ?? undefined,
    type: p.tipo as CustomerProfile['type'],
    status: p.status,
    createdAt: p.criadoEm,
    lastActivityAt: p.ultimaAtividadeEm ?? undefined,
    totalRevenue: p.receitaTotal,
    totalPending: p.pendenteTotal,
    purchasedServices: p.servicos.map((s) => ({
      id: s.id,
      title: s.titulo,
      amount: s.valor,
      date: s.data,
      status: s.status as 'pago' | 'pendente' | 'cancelado',
      category: s.categoria,
    })),
    timeline: p.linhaTempo.map((t) => ({
      id: t.id,
      type: t.tipo as CustomerProfile['timeline'][number]['type'],
      title: t.titulo,
      description: t.descricao,
      timestamp: t.quando,
      amount: t.valor,
    })),
    notes: p.observacoes ?? undefined,
    portalAccessCode: p.codigoAcessoPortal ?? undefined,
    assignedTo: p.responsavel ?? undefined,
    source: p.origem ?? undefined,
  };
}

function paraCustomerInvoice(f: FaturaContrato): CustomerInvoice {
  return {
    id: f.id,
    invoiceNumber: f.numero,
    clientId: f.clienteId,
    clientSlug: f.clienteSlug ?? undefined,
    customerPhone: f.telefoneConsumidor,
    customerName: f.nomeConsumidor,
    customerEmail: f.emailConsumidor ?? undefined,
    title: f.titulo,
    description: f.descricao ?? undefined,
    amount: f.valor,
    dueDate: f.vencimento,
    status: f.status as CustomerInvoice['status'],
    paymentMethodType: (f.tipoMetodo as CustomerInvoice['paymentMethodType']) ?? undefined,
    pixCode: f.codigoPix ?? undefined,
    cardPaymentUrl: f.urlPagamentoCartao ?? undefined,
    paidAt: f.pagoEm ?? undefined,
    createdAt: f.criadoEm,
    receiptUrl: f.urlComprovante ?? undefined,
  };
}

/** Reexportado para quem precisa converter HTML <-> spec fora daqui. */
export { htmlDoSpec, specDeHtmlBruto };
